import {defineStore} from 'pinia';
import {ref} from 'vue';
import {v4 as uuid} from 'uuid';
import type {BadgeColor} from "~/components/global/utils/badgeUtils";
import type dayjs from "dayjs";
import type {IconPropType} from "~/components/global/utils/iconPropUtil";
import {useNotificationStore} from "./notification";

export interface ToastBadge {
    text: string;
    icon: IconPropType;
    color: BadgeColor;
    outlined: boolean;
}

export interface ToastButton {
    text: string,
    callback: () => void;
}

export interface Toast {
    id: string;
    notificationId: string | null;
    title: string | null;
    message: string;
    timestamp: dayjs.Dayjs | null;
    icon: IconPropType;
    iconColor: BadgeColor;
    avatar: string | null;
    type: '' | 'success' | 'warning' | 'danger' | 'custom';
    closable: boolean;
    duration: number; // Duration in milliseconds
    remaining: number; // Remaining time in milliseconds
    position: 'top-left' | 'top' | 'top-right' | 'bottom-left' | 'bottom' | 'bottom-right';
    html: boolean;
    button: ToastButton | null;
    showTimestamp: boolean;
    badges: ToastBadge[] | null;
    validations: ToastBadge[] | null;
}

export const useToastStore = defineStore('toast', () => {
  const toasts = ref<Toast[]>([]);
  const notificationStore = useNotificationStore();

  const addToast = (toast: Omit<Toast, 'id'>) => {
    const id = uuid(); // Simple ID generation based on timestamp
    toasts.value.push({...toast, id, remaining: toast.duration});
  };

  const removeToast = (id: string) => {
    const index = toasts.value.findIndex((toast) => toast.id === id);
    if (index !== -1) {
      const toast = toasts.value[index];
      toasts.value.splice(index, 1);

      if (toast.notificationId) {
        notificationStore.setHasUnreadNotifications(true);
      }
    }
  };

  return {toasts, addToast, removeToast};
});